// src/directives/tooltip.js

const defaults = {
  message: "",
  position: "top", // 'top' | 'bottom' | 'left' | 'right'
  offset: 8,
  showDelay: 80,
  hideDelay: 80,
  class: undefined,
  arrow: true,
};

function resolveOpts(binding) {
  const v = binding.value;
  const base = typeof v === "string" ? { message: v } : v || {};
  return { ...defaults, ...base };
}

function createTooltipEl(id, clsExtra, withArrow) {
  const el = document.createElement("div");
  el.id = id;
  el.role = "tooltip";
  el.style.position = "fixed";
  el.style.pointerEvents = "none";
  el.style.zIndex = "9999";
  el.style.opacity = "0";
  el.style.transform = "translate3d(0,0,0)";
  el.className = [
    "px-2 py-1 rounded text-xs shadow-lg max-w-[80vw] break-words",
    "bg-gray-900 text-white",
    "transition-opacity duration-150 ease-out",
    "whitespace-pre-line",
    clsExtra || "",
  ]
    .join(" ")
    .trim();

  let arrow = null;
  if (withArrow) {
    arrow = document.createElement("div");
    arrow.style.position = "absolute";
    arrow.className = "w-2 h-2 rotate-45";
    // pour matcher la couleur de fond du conteneur, peu importe les classes ajoutées
    arrow.style.background = "inherit";
    el.appendChild(arrow);
  }

  document.body.appendChild(el);
  return { el, arrow };
}

function positionTooltip(target, state) {
  const { elTT, elArrow, opts } = state;
  const rect = target.getBoundingClientRect();
  const ttRect = elTT.getBoundingClientRect();
  const margin = 8; // padding viewport
  let x = 0,
    y = 0;

  if (elArrow) {
    // reset anchors de la flèche uniquement si elle existe
    elArrow.style.left = "";
    elArrow.style.right = "";
    elArrow.style.top = "";
    elArrow.style.bottom = "";
  }

  const arrowOffset = 6; // distance interne depuis le bord du tooltip

  switch (opts.position) {
    case "bottom":
      x = rect.left + rect.width / 2 - ttRect.width / 2;
      y = rect.bottom + opts.offset;
      if (elArrow) {
        elArrow.style.top = "-4px";
        elArrow.style.left = `${Math.max(arrowOffset, ttRect.width / 2 - 4)}px`;
      }
      break;
    case "left":
      x = rect.left - ttRect.width - opts.offset;
      y = rect.top + rect.height / 2 - ttRect.height / 2;
      if (elArrow) {
        elArrow.style.right = "-4px";
        elArrow.style.top = `${Math.max(arrowOffset, ttRect.height / 2 - 4)}px`;
      }
      break;
    case "right":
      x = rect.right + opts.offset;
      y = rect.top + rect.height / 2 - ttRect.height / 2;
      if (elArrow) {
        elArrow.style.left = "-4px";
        elArrow.style.top = `${Math.max(arrowOffset, ttRect.height / 2 - 4)}px`;
      }
      break;
    case "top":
    default:
      x = rect.left + rect.width / 2 - ttRect.width / 2;
      y = rect.top - ttRect.height - opts.offset;
      if (elArrow) {
        elArrow.style.bottom = "-4px";
        elArrow.style.left = `${Math.max(arrowOffset, ttRect.width / 2 - 4)}px`;
      }
      break;
  }

  // clamp à l'écran
  x = Math.max(margin, Math.min(x, window.innerWidth - ttRect.width - margin));
  y = Math.max(
    margin,
    Math.min(y, window.innerHeight - ttRect.height - margin),
  );

  elTT.style.left = `${x}px`;
  elTT.style.top = `${y}px`;
}

function setMessage(state, msg) {
  const { elTT, elArrow } = state;
  // on reconstruit le contenu : d'abord le texte, puis on remet la flèche si elle existe
  elTT.innerHTML = "";
  const span = document.createElement("span");
  span.textContent = msg;
  elTT.appendChild(span);
  if (elArrow) elTT.appendChild(elArrow);
}

function showTooltip(target, state) {
  if (state.hideTO) {
    clearTimeout(state.hideTO);
    state.hideTO = null;
  }
  if (state.showTO) return;
  state.showTO = window.setTimeout(() => {
    positionTooltip(target, state);
    state.elTT.style.opacity = "1";
    state.visible = true;
    state.showTO = null;
  }, state.opts.showDelay);
}

function hideTooltip(state) {
  if (state.showTO) {
    clearTimeout(state.showTO);
    state.showTO = null;
  }
  if (state.hideTO) return;
  state.hideTO = window.setTimeout(() => {
    state.elTT.style.opacity = "0";
    state.visible = false;
    state.hideTO = null;
  }, state.opts.hideDelay);
}

function attach(el, binding) {
  const opts = resolveOpts(binding);
  if (!opts.message) return;

  const id =
    "tt-" +
    ((window.crypto && crypto.randomUUID && crypto.randomUUID()) ||
      Math.random().toString(36).slice(2));
  const { el: elTT, arrow: elArrow } = createTooltipEl(
    id,
    opts.class,
    opts.arrow,
  );

  const state = {
    id,
    elTT,
    elArrow,
    showTO: null,
    hideTO: null,
    visible: false,
    opts,
  };
  el.__tooltipState = state;

  setMessage(state, opts.message);
  el.setAttribute("aria-describedby", id);

  const onEnter = () => showTooltip(el, state);
  const onLeave = () => hideTooltip(state);
  const onReposition = () => {
    if (state.visible) positionTooltip(el, state);
  };

  el.addEventListener("mouseenter", onEnter);
  el.addEventListener("mouseleave", onLeave);
  el.addEventListener("focus", onEnter, true);
  el.addEventListener("blur", onLeave, true);
  el.addEventListener("touchstart", onEnter, { passive: true });
  el.addEventListener("touchend", onLeave);

  window.addEventListener("scroll", onReposition, true);
  window.addEventListener("resize", onReposition);

  el.__tooltipCleanup = () => {
    el.removeEventListener("mouseenter", onEnter);
    el.removeEventListener("mouseleave", onLeave);
    el.removeEventListener("focus", onEnter, true);
    el.removeEventListener("blur", onLeave, true);
    el.removeEventListener("touchstart", onEnter);
    el.removeEventListener("touchend", onLeave);
    window.removeEventListener("scroll", onReposition, true);
    window.removeEventListener("resize", onReposition);
  };
}

function update(el, binding) {
  const state = el.__tooltipState;
  const next = resolveOpts(binding);
  if (!state) {
    attach(el, binding);
    return;
  }
  state.opts = next;
  // si l'état "arrow" change, on reconstruit l'élément proprement
  if (!!state.elArrow !== !!next.arrow) {
    // détruire et recréer proprement
    detach(el);
    attach(el, binding);
    return;
  }
  setMessage(state, next.message);
  if (state.visible) requestAnimationFrame(() => positionTooltip(el, state));
}

function detach(el) {
  const state = el.__tooltipState;
  if (!state) return;
  el.__tooltipCleanup && el.__tooltipCleanup();
  el.removeAttribute("aria-describedby");
  state.elTT.remove();
  delete el.__tooltipState;
  delete el.__tooltipCleanup;
}

const vTooltip = {
  mounted: attach,
  updated: update,
  unmounted: detach,
};

export default vTooltip;
